import { useEffect, useState, useCallback } from "react"
import { useParams } from "react-router-dom"
import { Check, Copy, Download, Printer, ScanSearch } from "lucide-react"
import api from "../lib/api"
import { Button } from "@/components/ui/button"
import { PageEmpty, PageHeader, PageSpinner } from "@/components/page"
import { AnimatedCheck } from "@/components/motion/AnimatedCheck"

interface Certificate {
  id: string
  certificate_code: string
  course_title: string
  category: string
  difficulty_level: string
  estimated_hours: number
  student_name: string
  issued_at: string
}

export default function CertificatePage() {
  const { code } = useParams()
  const [cert, setCert] = useState<Certificate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const loadCertificate = useCallback(async () => {
    try {
      const { data } = await api.get(`/academy/certificates/verify/${code}`)
      setCert(data.data)
    } catch {
      setError("Certificado no encontrado")
    } finally {
      setLoading(false)
    }
  }, [code])

  useEffect(() => {
    loadCertificate()
  }, [loadCertificate])

  const [downloading, setDownloading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const [downloadError, setDownloadError] = useState(false)
  const verifyUrl = `${window.location.origin}/certificates/${code}`

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl)
      setCopyFailed(false)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      setCopyFailed(true)
    }
  }

  const handleDownload = async () => {
    setDownloading(true)
    setDownloadError(false)
    try {
      const res = await api.get(`/academy/certificates/${code}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `certificado-${code}.png`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      // Sin imagen renderizada (plantilla no configurada) o error de red: se avisa
      // y se ofrece imprimir esta vista como alternativa explícita.
      setDownloadError(true)
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="section-shell py-16">
        <PageSpinner label="Cargando certificado" />
      </div>
    )
  }

  if (error || !cert) {
    return (
      <div className="section-shell mx-auto max-w-xl space-y-6 py-16">
        <PageHeader title="Certificado no encontrado" />
        <PageEmpty
          icon={<ScanSearch className="h-7 w-7" />}
          title="No pudimos verificar este código"
          description={
            <>
              El código <span className="font-mono text-foreground">{code}</span> no existe o ya no está disponible para verificación pública. Revisa que lo hayas copiado completo.
            </>
          }
        />
      </div>
    )
  }

  return (
    <div className="section-shell py-10">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <PageHeader title="Certificado" />

        <div className="flex shrink-0 flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={handleCopyLink} className="h-11 px-5 text-sm font-medium">
            {copied ? <Check key="ok" className="motion-pop mr-2 h-4 w-4" aria-hidden="true" /> : <Copy key="copy" className="mr-2 h-4 w-4" aria-hidden="true" />}
            <span key={copied ? "ok" : "idle"} className={copied ? "motion-pop" : undefined} aria-live="polite">{copied ? "Enlace copiado" : "Copiar enlace de verificación"}</span>
          </Button>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="h-11 px-5 text-sm font-medium"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            {downloading ? "Descargando..." : "Descargar certificado"}
          </Button>
        </div>
      </div>

      {copyFailed && (
        <div role="alert" className="mb-4 rounded-lg border border-border bg-muted/50 p-4 text-sm print:hidden">
          <label htmlFor="verify-url" className="font-medium text-foreground">No se pudo copiar, selecciona el enlace:</label>
          <input id="verify-url" readOnly value={verifyUrl} onFocus={(e) => e.currentTarget.select()} className="mt-2 h-11 w-full rounded-lg border border-input bg-background px-3 text-sm sm:h-9" />
        </div>
      )}
      {downloadError && (
        <div role="alert" className="mb-4 flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm sm:flex-row sm:items-center sm:justify-between print:hidden">
          <p className="text-foreground">No pudimos generar la imagen del certificado. Puedes imprimir o guardar esta página como PDF.</p>
          <Button type="button" variant="outline" onClick={() => window.print()} className="h-11 shrink-0 px-4 text-sm font-medium sm:h-9">
            <Printer className="mr-2 h-4 w-4" aria-hidden="true" />
            Imprimir
          </Button>
        </div>
      )}

      <div className="surface-panel overflow-hidden rounded-xl print:shadow-none">
        <div className="grid gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="border-b border-sidebar-border bg-sidebar p-6 lg:border-b-0 lg:border-r">
            <div className="flex h-full flex-col justify-between gap-8">
              <div className="space-y-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-sidebar-border bg-sidebar-accent text-sidebar-primary">
                  <AnimatedCheck className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-sidebar-foreground">
                    Verificado
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-sidebar-foreground/85">
                    Emitido por SateLab Academy. Cualquier persona puede comprobar su autenticidad con este código.
                  </p>
                </div>
              </div>

              <div className="space-y-4 text-sm">
                <div className="rounded-lg border border-sidebar-border bg-sidebar-accent/40 p-4">
                  <p className="text-micro font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/80">
                    Código de verificación
                  </p>
                  <p className="mt-2 break-all font-mono text-sidebar-foreground select-all">
                    {cert.certificate_code}
                  </p>
                </div>
                <div className="rounded-lg border border-sidebar-border bg-sidebar-accent/40 p-4">
                  <p className="text-micro font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/80">
                    Emitido
                  </p>
                  <p className="mt-2 text-sidebar-foreground">
                    {new Date(cert.issued_at).toLocaleDateString("es-CO", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </aside>

          <section className="relative p-6 sm:p-10 lg:p-14">
            <div className="relative">
              <div className="flex flex-col gap-4 border-b border-border pb-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                    Certificado de finalización
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    SateLab Academy
                  </p>
                </div>

              </div>

              <div className="space-y-8 py-10">
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Se certifica que
                  </p>
                  <h3 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
                    {cert.student_name}
                  </h3>
                </div>

                <div className="space-y-4">
                  <p className="max-w-3xl text-base leading-8 text-muted-foreground sm:text-lg">
                    Ha completado satisfactoriamente el programa{" "}
                    <span className="font-medium text-foreground">{cert.course_title}</span>,
                    cumpliendo los requisitos académicos y técnicos definidos por
                    SateLab Academy.
                  </p>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-xl border border-border bg-muted/50 p-5">
                      <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        Categoría
                      </p>
                      <p className="mt-3 text-xl font-medium text-foreground">
                        {cert.category || "General"}
                      </p>
                    </div>
                    <div className="rounded-xl border border-border bg-muted/50 p-5">
                      <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        Nivel
                      </p>
                      <p className="mt-3 text-xl font-medium text-foreground">
                        {cert.difficulty_level}
                      </p>
                    </div>
                    <div className="rounded-xl border border-border bg-muted/50 p-5">
                      <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        Horas
                      </p>
                      <p className="mt-3 text-xl font-medium text-foreground">
                        {cert.estimated_hours}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
